<script setup lang="ts">
  import { Button, Toast, ToastSize, ToastType } from '@travelata-libs/ui-kit';
  import { computed, ref, watch } from 'vue';
  import {
    DEFAULT_TOURISTS,
    type DestinationPrices,
    type StayCriteria,
    type TouristsSelection,
    type Selection,
  } from '@/entities/searchCriteria';
  import { FieldSize, type SearchFormGoal, type SearchFormTracking } from '@/entities/searchForm';
  import { DateFromField, StayDatesField, formatCheckInLabel } from '@/features/searchForm/dateFrom';
  import { DepartureCityField } from '@/features/searchForm/departureCity';
  import { DestinationField } from '@/features/searchForm/destination';
  import { NightsRangeField } from '@/features/searchForm/nightsRange';
  import { TouristsRoomsField } from '@/features/searchForm/touristsRooms';
  import { useViewport, Breakpoint } from '@/shared/composables';
  import type { DepartureCity, NightsRange } from '@/shared/model';
  import { SEARCH_FORM_TEXTS } from '../config/texts';
  import { type SearchFormCriteria, type SearchFormExpose } from '../model/types';
  import { useCrossFieldCriteria } from '../model/useCrossFieldCriteria';
  import { useSearchFormData } from '../model/useSearchFormData';
  import { useSearchFormGoals } from '../model/useSearchFormGoals';

  interface SearchFormProps {
    modelValue: SearchFormCriteria;
    prices?: DestinationPrices;
    destinationDisabled?: boolean;
    departureDisabled?: boolean;
    disableCriteriaDefaults?: boolean;
    isVisibleRoomButton?: boolean;
    isVisibleDeparture?: boolean;
    formUrl?: string;
    searchUrl?: string;
    resortsUrl?: string;
    size?: `${FieldSize}`;
    searchBtnText?: string;
    dateFieldLabel?: string;
    touristsFieldLabel?: string;
    touristsWordForms?: string[];
    nightsSummaryPrefix?: string;
    roundedBottom?: boolean;
    isStayCalendar?: boolean;
    tracking?: SearchFormTracking;
  }

  const props = withDefaults(defineProps<SearchFormProps>(), {
    prices: undefined,
    destinationDisabled: false,
    departureDisabled: false,
    disableCriteriaDefaults: false,
    formUrl: '',
    searchUrl: '',
    resortsUrl: '',
    isVisibleDeparture: true,
    size: FieldSize.M,
    searchBtnText: SEARCH_FORM_TEXTS.searchBtn,
    dateFieldLabel: undefined,
    touristsFieldLabel: undefined,
    touristsWordForms: undefined,
    nightsSummaryPrefix: undefined,
    isStayCalendar: false,
    tracking: undefined,
  });

  interface SearchFormEmits {
    (e: 'update:modelValue', searchCriteria: SearchFormCriteria): void;
    (e: 'search', searchCriteria: SearchFormCriteria): void;
    (e: 'track', goal: SearchFormGoal): void;
  }

  const emit = defineEmits<SearchFormEmits>();

  const { countries, cities, defaultCriteria, failed: isDataFailed } = useSearchFormData(props.formUrl);
  const { isSmaller } = useViewport();

  const isDataReady = computed(() => {
    const hasDeparture = !props.isVisibleDeparture || cities.value.length > 0;
    return hasDeparture && countries.value.length > 0 && Boolean(props.modelValue.destination);
  });
  const isLoading = ref(!isDataReady.value);
  const goals = useSearchFormGoals(props, (goal) => emit('track', goal), isLoading);
  watch([isDataReady, isDataFailed], ([isReady, isFailed]) => {
    if (isReady || isFailed) {
      isLoading.value = false;
    }
  });

  const isErrorToastClosed = ref(false);
  const isErrorToastVisible = computed(() => isDataFailed.value && !isErrorToastClosed.value);

  function closeErrorToast(): void {
    isErrorToastClosed.value = true;
  }

  function reloadPage(): void {
    window.location.reload();
  }

  const DEFAULT_NIGHTS: NightsRange = { from: 7, to: 10 };
  const MODEL_VALUE_EVENT = 'update:modelValue';
  function defaultTourists(): TouristsSelection {
    return DEFAULT_TOURISTS;
  }

  const dateRange = computed({
    get: () => props.modelValue.dateRange,
    set: (value) => emit(MODEL_VALUE_EVENT, { ...props.modelValue, dateRange: value }),
  });
  const tourists = computed({
    get: () => props.modelValue.tourists || defaultTourists(),
    set: (value) => emit(MODEL_VALUE_EVENT, { ...props.modelValue, tourists: value }),
  });
  const nights = computed({
    get: () => props.modelValue.nights || DEFAULT_NIGHTS,
    set: (value) => emit(MODEL_VALUE_EVENT, { ...props.modelValue, nights: value }),
  });
  function resolveDefaults(
    city: DepartureCity | null,
    selection: Selection | null,
    model: SearchFormCriteria,
  ): Partial<SearchFormCriteria> {
    return props.disableCriteriaDefaults ? {} : criteria.resolveDefaults(city, selection, model);
  }

  const fromCity = computed({
    get: () => props.modelValue.fromCity,
    set: (value) => {
      emit(MODEL_VALUE_EVENT, {
        ...props.modelValue,
        fromCity: value,
        ...resolveDefaults(value, destination.value, props.modelValue),
      });
    },
  });
  const destination = computed({
    get: () => props.modelValue.destination,
    set: (value: Selection | null) => {
      if (value?.kind === 'none') return;
      emit(MODEL_VALUE_EVENT, {
        ...props.modelValue,
        destination: value,
        ...resolveDefaults(fromCity.value, value, props.modelValue),
      });
    },
  });

  const isAdaptive = isSmaller(Breakpoint.L);

  const searchButtonText = computed(() => (isAdaptive.value ? '' : props.searchBtnText));

  const criteria = useCrossFieldCriteria({
    countries,
    defaultCriteria,
    destination,
    nights,
    dateRange,
  });

  const stayCriteria = computed<StayCriteria>({
    get: () => ({ dateRange: props.modelValue.dateRange, nights: props.modelValue.nights }),
    set: (value) => emit(MODEL_VALUE_EVENT, { ...props.modelValue, ...value }),
  });

  function markTouchedStay(): void {
    criteria.markTouchedDate();
    criteria.markTouchedNights();
  }

  const checkInLabel = computed<string | undefined>(() =>
    dateRange.value ? formatCheckInLabel(dateRange.value) : undefined,
  );

  type ValidatableField = { validate: () => boolean } | null;
  type DestinationFieldRef = { validate: () => boolean; clear: () => void } | null;
  const fromCityRef = ref<ValidatableField>(null);
  const destinationRef = ref<DestinationFieldRef>(null);
  const dateFromRef = ref<ValidatableField>(null);
  const nightsRef = ref<ValidatableField>(null);
  const touristsRef = ref<ValidatableField>(null);
  const searchButtonContainerRef = ref<HTMLElement | null>(null);

  function validate(): boolean {
    const results = [
      fromCityRef.value?.validate() ?? true,
      destinationRef.value?.validate() ?? true,
      dateFromRef.value?.validate() ?? true,
      nightsRef.value?.validate() ?? true,
      touristsRef.value?.validate() ?? true,
    ];
    return results.every(Boolean);
  }

  function submit(): void {
    goals.trackSearchClick();
    if (props.isStayCalendar && !(dateFromRef.value?.validate() ?? true)) {
      return;
    }
    emit('search', props.modelValue);
  }

  function reset(): void {
    destinationRef.value?.clear();
    emit(MODEL_VALUE_EVENT, {
      ...props.modelValue,
      fromCity: null,
      destination: null,
      nights: { ...DEFAULT_NIGHTS },
      dateRange: null,
      tourists: defaultTourists(),
    });
    criteria.reset();
  }

  defineExpose<SearchFormExpose>({
    validate,
    submit,
    reset,
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    :class="['search-form', `search-form_size-${size}`, { 'search-form_rounded-bottom': roundedBottom }]"
    :aria-busy="isLoading ? 'true' : undefined"
    data-testid="search-form"
  >
    <div class="search-form__bar">
      <div
        :class="['search-form__card']"
        :inert="isLoading"
      >
        <div
          v-if="isVisibleDeparture"
          class="search-form__cell search-form__cell_origin"
        >
          <DepartureCityField
            ref="fromCityRef"
            v-model="fromCity"
            :size="size"
            :loading="isLoading"
            :cities="cities"
            :disabled="departureDisabled || isDataFailed"
          />
        </div>

        <span
          v-if="isVisibleDeparture"
          class="search-form__separator"
          aria-hidden="true"
        />

        <div
          :class="[
            'search-form__cell',
            'search-form__cell_destination',
            {
              'search-form__cell_destination-no-origin': !isVisibleDeparture,
            },
          ]"
        >
          <DestinationField
            ref="destinationRef"
            v-model="destination"
            :size="size"
            :loading="isLoading"
            :countries="countries"
            :disabled="destinationDisabled || isDataFailed"
            :prices="prices"
            :search-url="searchUrl"
            :resorts-url="resortsUrl"
          />
        </div>

        <span
          class="search-form__separator"
          aria-hidden="true"
        />

        <div :class="['search-form__cell', 'search-form__cell_date', { 'search-form__cell_stay': isStayCalendar }]">
          <StayDatesField
            v-if="isStayCalendar"
            ref="dateFromRef"
            v-model="stayCriteria"
            :size="size"
            :loading="isLoading"
            :disabled="isDataFailed"
            :submit-element="searchButtonContainerRef"
            @manual="markTouchedStay"
            @track="goals.track"
          />
          <DateFromField
            v-else
            ref="dateFromRef"
            v-model="dateRange"
            :size="size"
            :loading="isLoading"
            :disabled="isDataFailed"
            :field-label="dateFieldLabel"
            @manual="criteria.markTouchedDate"
            @track="goals.track"
          />
        </div>

        <span
          v-if="!isStayCalendar"
          class="search-form__separator"
          aria-hidden="true"
        />

        <div
          v-if="!isStayCalendar"
          class="search-form__cell search-form__cell_nights"
        >
          <NightsRangeField
            ref="nightsRef"
            v-model="nights"
            :size="size"
            :loading="isLoading"
            :disabled="isDataFailed"
            :check-in-label="checkInLabel"
            :summary-prefix="nightsSummaryPrefix"
            @manual="criteria.markTouchedNights"
          />
        </div>

        <span
          class="search-form__separator"
          aria-hidden="true"
        />

        <div
          :class="[
            'search-form__cell',
            'search-form__cell_guests',
            {
              'search-form__cell_guests-no-rooms': !isVisibleRoomButton,
            },
          ]"
        >
          <TouristsRoomsField
            ref="touristsRef"
            v-model="tourists"
            :size="size"
            :loading="isLoading"
            :is-visible-add-button="isVisibleRoomButton"
            :field-label="touristsFieldLabel"
            :tourists-word-forms="touristsWordForms"
            :disabled="isDataFailed"
          />
        </div>
        <div
          ref="searchButtonContainerRef"
          class="search-form__btn-container"
        >
          <Button
            class="search-form__search-btn"
            data-testid="search-form-search-btn"
            appearance="primary"
            :icon-left="isAdaptive ? 'search' : undefined"
            :disabled="isDataFailed"
            @click="submit"
          >
            <template
              v-if="searchButtonText"
              #default
            >
              {{ searchButtonText }}
            </template>
          </Button>
        </div>
      </div>
    </div>

    <div
      v-if="isErrorToastVisible"
      class="search-form__toast"
    >
      <Toast
        :type="ToastType.Error"
        :size="ToastSize.L"
        :title="SEARCH_FORM_TEXTS.loadErrorTitle"
        :description="SEARCH_FORM_TEXTS.loadErrorDescription"
        closable
        @close="closeErrorToast"
      >
        <template #footer>
          <Button
            appearance="tertiary"
            icon-left="refresh"
            @click="reloadPage"
          >
            {{ SEARCH_FORM_TEXTS.reloadBtn }}
          </Button>
        </template>
      </Toast>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .search-form {
    $root: &;

    @include header-background;

    box-sizing: border-box;
    display: flex;
    justify-content: center;
    padding: var(--spacing-m) 0 var(--spacing-l) 0;
    position: relative;
    width: 100%;
    z-index: 2;

    &_rounded-bottom {
      border-bottom-left-radius: var(--rounding-xl);
      border-bottom-right-radius: var(--rounding-xl);
    }

    &__bar {
      max-width: $max-width-xl;
      width: 100%;
    }

    &__card {
      align-items: center;
      background: var(--color-bg-primary);
      border-radius: var(--rounding-l);
      display: flex;
      flex: 1 1 auto;
      padding-right: var(--spacing-xs);
    }

    &__cell {
      flex-grow: 1;

      &_origin {
        --field-skeleton-label-width: 40px;
        --field-skeleton-value-width: 64px;

        flex-basis: 109px;
        flex-grow: 2.5;

        ::v-deep .field__control {
          border-radius: var(--rounding-l) 0 0 var(--rounding-l);
        }
      }

      &_destination {
        --field-skeleton-label-width: 40px;
        --field-skeleton-value-width: 148px;

        flex-basis: 171px;
        flex-grow: 4.4;

        &-no-origin {
          ::v-deep .field__control {
            border-radius: var(--rounding-l) 0 0 var(--rounding-l);
          }
        }
      }

      &_date {
        --field-skeleton-label-width: 72px;
        --field-skeleton-value-width: 94px;

        flex-basis: 124px;
        flex-grow: 1;
      }

      &_stay {
        --field-skeleton-value-width: 196px;

        flex-basis: 248px;
        flex-grow: 2;
      }

      &_nights {
        --field-skeleton-label-width: 78px;
        --field-skeleton-value-width: 101px;

        flex-basis: 124px;
        flex-grow: 1;
      }

      &_guests {
        --field-skeleton-label-width: 72px;
        --field-skeleton-value-width: 78px;

        flex-basis: 116px;
        flex-grow: 1;
      }

      &_guests-no-rooms {
        flex-basis: 141px;
        flex-grow: 1.7;
      }
    }

    &__separator {
      align-self: center;
      background-color: var(--color-neutral-20);
      flex: 0 0 1px;
      height: 54px;
      width: 1px;
    }

    &__btn-container {
      box-sizing: border-box;
      max-width: 188px;
      padding-left: var(--spacing-xs);
      width: 100%;
    }

    &__search-btn {
      width: 100%;
    }

    &_size-l {
      #{$root}__search-btn {
        height: 48px;
      }
      #{$root}__separator {
        height: 64px;
      }
    }

    @media (max-width: $breakpoint-l) {
      &__btn-container {
        max-width: min-content;
        width: auto;
      }

      &__search-btn {
        width: auto;
      }
    }

    &__toast {
      bottom: 50px;
      position: fixed;
      right: 50px;
      z-index: 1000;
    }

    @include responsive-max-width('&__bar');
  }
</style>
